import React, { useState } from "react";
import ProductPageLayout from "@/components/ProductPageLayout";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { motion } from "framer-motion";
import { 
  Award, 
  Trophy, 
  Star, 
  Target, 
  Users, 
  CheckCircle, 
  Settings, 
  Crown,
  Medal,
  Sparkles,
  Shield,
  Zap,
  ArrowRight,
  TrendingUp,
  Heart,
  Gift
} from "lucide-react";
import { DemoBookingModal } from "@/components/DemoBookingModal";
import TrustLogosSection from "@/components/trust/TrustLogosSection";
import HeroBadgesSection from "@/components/hero/HeroBadgesSection";

const Awards = () => {
  const [isDemoModalOpen, setIsDemoModalOpen] = useState(false);

  const features = [
    {
      icon: <Settings className="h-6 w-6" />,
      title: "Custom Award Creation",
      description: "Design awards tailored to your values and culture",
      features: ["Custom templates", "Value alignment", "Award images", "Description builder"],
      color: "from-blue-500 to-cyan-500"
    },
    {
      icon: <Shield className="h-6 w-6" />,
      title: "Multi-Level Approvals",
      description: "Configure sophisticated approval workflows",
      features: ["Up to 4 levels", "Manager cascading", "Custom approvers", "Auto-approve"],
      color: "from-purple-500 to-pink-500"
    },
    {
      icon: <Gift className="h-6 w-6" />,
      title: "Budget Management",
      description: "Control spending with flexible budget controls",
      features: ["Total budgets", "Refresh cycles", "Per-award limits", "Point allocation"],
      color: "from-emerald-500 to-teal-500"
    },
    {
      icon: <Users className="h-6 w-6" />,
      title: "Smart Giver Selection",
      description: "Define who can give awards with granular controls",
      features: ["Role-based access", "Department filters", "Location-based", "Custom groups"],
      color: "from-orange-500 to-amber-500"
    },
    {
      icon: <Sparkles className="h-6 w-6" />,
      title: "Automated Posts",
      description: "Awards create special recognition posts automatically",
      features: ["Auto-publish", "Feed integration", "Social sharing", "Celebration posts"],
      color: "from-pink-500 to-rose-500"
    },
    {
      icon: <Trophy className="h-6 w-6" />,
      title: "Award Analytics",
      description: "Track award impact and recognition trends",
      features: ["Usage metrics", "Engagement data", "Budget tracking", "Winner insights"],
      color: "from-indigo-500 to-blue-500"
    }
  ];

  const awardTypes = [
    {
      icon: <Star className="h-8 w-8" />,
      title: "Employee of the Month",
      description: "Monthly recognition for outstanding performance",
      color: "from-orange-400 to-red-500"
    },
    {
      icon: <Trophy className="h-8 w-8" />,
      title: "Innovation Excellence",
      description: "Celebrate creative solutions and breakthrough ideas",
      color: "from-blue-500 to-purple-600"
    },
    {
      icon: <Crown className="h-8 w-8" />,
      title: "Leadership Awards",
      description: "Honor exceptional leadership and mentorship",
      color: "from-yellow-400 to-orange-500"
    },
    {
      icon: <Heart className="h-8 w-8" />,
      title: "Values Champion",
      description: "Recognize employees who embody company values",
      color: "from-pink-400 to-rose-500"
    }
  ];

  const benefits = [
    {
      icon: <TrendingUp className="h-6 w-6" />,
      title: "78% More Visibility",
      description: "Organizations see higher recognition engagement with structured award programs"
    },
    {
      icon: <Target className="h-6 w-6" />,
      title: "3x Better Value Alignment",
      description: "Awards help reinforce company values and strategic priorities"
    },
    {
      icon: <Medal className="h-6 w-6" />,
      title: "65% Higher Motivation",
      description: "Employees report increased motivation with prestigious awards"
    }
  ];

  return (
    <ProductPageLayout
      title="Employee Awards Software - Custom Recognition Awards Platform"
      description="Create and manage custom employee awards with Assembly's Awards platform. Design prestigious recognition programs, configure approval workflows, and celebrate outstanding achievements."
      keywords="employee awards software, custom awards program, recognition platform, award management system, employee achievement awards"
      ctaTitle="Ready to Launch Your Awards Program?"
      ctaDescription="See how Assembly's Awards platform can help you create meaningful recognition programs that drive excellence."
      ctaButtonText="Get a Demo"
      ctaVariant="blue"
      onCtaClick={() => setIsDemoModalOpen(true)}
    >
      {/* Full Height Hero Section */}
      <div className="relative bg-gradient-to-br from-slate-50 via-gray-50 to-blue-50/30 font-sans overflow-hidden min-h-screen">
        {/* Subtle Background */}
        <div className="absolute inset-0 overflow-hidden">
          <div className="absolute -top-40 -right-40 w-[600px] h-[600px] bg-gradient-to-br from-blue-500/8 via-purple-500/5 to-blue-400/4 rounded-full blur-3xl animate-pulse"></div>
          <div className="absolute top-1/3 -left-40 w-[500px] h-[500px] bg-gradient-to-br from-purple-400/6 via-blue-500/5 to-slate-400/3 rounded-full blur-3xl animate-pulse" style={{
            animationDelay: '1s'
          }}></div>
        </div>
        
        {/* Dot Grid Pattern */}
        <div className="absolute inset-0 opacity-[0.15]">
          <div className="absolute inset-0" style={{
            backgroundImage: `radial-gradient(circle at 2px 2px, rgba(59, 130, 246, 0.3) 2px, transparent 0)`,
            backgroundSize: '48px 48px'
          }}></div>
        </div>

        <section className="relative flex items-center justify-center py-8 lg:py-10 px-4 lg:px-8 min-h-screen pt-20 lg:pt-24">
          <div className="container mx-auto px-4 2xl:px-12 max-w-none lg:max-w-7xl xl:max-w-[1400px] 2xl:max-w-[1600px] relative px-6 lg:px-6 xl:px-8 pb-3 lg:pb-4">
            <div className="flex flex-col lg:grid lg:grid-cols-7 gap-6 lg:gap-4 items-start">
              {/* Left Section: Text Content */}
              <div className="flex flex-col items-start text-left lg:col-span-4 h-full justify-center space-y-4 lg:space-y-6">
                {/* Pills */}
                <motion.div 
                  className="flex flex-col sm:flex-row sm:flex-wrap items-start sm:items-center gap-2 sm:gap-3 mt-6 lg:mt-8 mb-3 lg:mb-4"
                  initial={{ opacity: 0, y: -20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.7 }}
                >
                  <div className="relative overflow-hidden px-3 py-1.5 rounded-full bg-black/5 border border-black/10 backdrop-blur-sm hover:bg-black/10 transition-all duration-300 hover:-translate-y-0.5">
                    <h1 className="text-xs font-normal text-gray-900 tracking-wide uppercase">Employee Awards Platform</h1>
                  </div>
                </motion.div>

                {/* Main Heading */}
                <div className="mb-4 lg:mb-6">
                <motion.h2 
                  className="text-4xl sm:text-5xl md:text-6xl lg:text-5xl xl:text-6xl font-bold text-black tracking-tight font-sans"
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.8, delay: 0.2 }}
                >
                  Awards that drive excellence and celebrate wins
                </motion.h2>
                </div>

                {/* Subheading */}
                <motion.p 
                  className="text-sm sm:text-base lg:text-base xl:text-lg text-gray-600 mb-6 lg:mb-8 leading-relaxed"
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.8, delay: 0.4 }}
                >
                  Assembly's Awards platform makes it easy to create prestigious employee awards with custom designs, flexible budgets, multi-level approvals, and automated recognition posts. Celebrate outstanding achievements and reinforce your company values.
                </motion.p>
                
                {/* Action Buttons */}
                <motion.div 
                  className="mb-4 lg:mb-6 w-full flex flex-col sm:flex-row gap-3"
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.8, delay: 0.6 }}
                >
                  <Button 
                    size="lg" 
                    className="w-full sm:w-auto bg-blue-600 hover:bg-blue-700 text-white shadow-sm hover:shadow-md transition-all duration-200" 
                    onClick={() => setIsDemoModalOpen(true)}
                  >
                    Book a Demo
                    <ArrowRight className="ml-2 h-4 w-4" />
                  </Button>
                </motion.div>
                
                {/* Trust Ratings */}
                <motion.div 
                  className="flex flex-col sm:flex-row gap-3 sm:gap-6 items-start sm:items-center w-full"
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.8, delay: 0.8 }}
                >
                  <div className="flex items-center gap-2">
                    <Star className="h-4 w-4 sm:h-5 sm:w-5 text-yellow-400 fill-yellow-400" />
                    <span className="text-sm font-semibold text-gray-700">4.9 / 5 on G2</span>
                    <img src="/lovable-uploads/e68d106f-69a9-45e8-8624-7f09b1ae1e6f.png" alt="G2 Logo" className="h-4 w-4 sm:h-5 sm:w-5" width={20} height={20} />
                  </div>
                  <a href="https://www.g2.com/products/assembly-2021-12-09/reviews" target="_blank" rel="noopener noreferrer" className="hover:opacity-80 transition-opacity">
                    <span className="text-sm font-semibold text-gray-500">3000+ reviews</span>
                  </a>
                </motion.div>
              </div>
              
              {/* Right Section: Hero Image */}
              <div className="relative w-full h-full flex items-center justify-center lg:col-span-3 mt-6 lg:mt-0">
                <div className="relative group">
                  <motion.img 
                    src="/lovable-uploads/7a25708b-ed0f-4dac-b930-a63b5e08fc22.png" 
                    alt="Employee awards showcase featuring recognition badges and achievement awards" 
                    className="w-full h-auto object-cover rounded-xl transition-transform duration-300 group-hover:scale-105" 
                    loading="eager" 
                    decoding="async" 
                    fetchPriority="high"
                    initial={{ opacity: 0, scale: 0.95 }}
                    animate={{ opacity: 1, scale: 1 }}
                    transition={{ duration: 0.8, delay: 0.4 }}
                  />
                </div>
              </div>
            </div>
            
            {/* Trust Section at Bottom */}
            <motion.div 
              className="text-center pt-8 lg:pt-12 mt-8 space-y-8"
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 1.0 }}
            >
              <p className="text-lg text-gray-600">
                The #1 Employee Awards Platform - Trusted by Leading Teams Worldwide
              </p>
              <HeroBadgesSection />
              <div className="pt-4">
                <TrustLogosSection />
              </div>
            </motion.div>
          </div>
        </section>
      </div>

      {/* Award Types Showcase */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-7xl">
          <div className="text-center mb-16">
            <h2 className="text-3xl lg:text-4xl font-bold text-gray-900 mb-6">
              Awards for Every Achievement
            </h2>
            <p className="text-xl text-gray-600 max-w-4xl mx-auto leading-relaxed">
              Create meaningful recognition programs that celebrate the achievements that matter most to your organization
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {awardTypes.map((award, index) => (
              <motion.div
                key={award.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                className="bg-white border border-gray-200 rounded-2xl p-6 text-center hover:shadow-lg transition-all duration-300 hover:-translate-y-1"
              >
                <div className={`w-16 h-16 bg-gradient-to-r ${award.color} rounded-full flex items-center justify-center mx-auto mb-4 text-white`}>
                  {award.icon}
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-3">{award.title}</h3>
                <p className="text-gray-600 leading-relaxed">
                  {award.description}
                </p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-20 bg-gradient-to-br from-slate-50 to-gray-50">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-7xl">
          <div className="text-center mb-16">
            <h2 className="text-3xl lg:text-4xl font-bold text-gray-900 mb-6">
              Complete Awards Management Platform
            </h2>
            <p className="text-xl text-gray-600 max-w-4xl mx-auto leading-relaxed">
              Everything you need to create, manage, and celebrate meaningful employee awards
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {features.map((feature, index) => (
              <motion.div
                key={feature.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                className="bg-white border border-gray-200 rounded-2xl p-6 hover:shadow-lg transition-shadow duration-300"
              >
                <div className={`w-12 h-12 rounded-xl bg-gradient-to-br ${feature.color} flex items-center justify-center text-white mb-4`}>
                  {feature.icon}
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-3">{feature.title}</h3>
                <p className="text-gray-600 mb-4 leading-relaxed">{feature.description}</p>
                <ul className="space-y-2">
                  {feature.features.map((item) => (
                    <li key={item} className="flex items-center text-sm text-gray-600">
                      <CheckCircle className="h-4 w-4 text-blue-500 mr-2 flex-shrink-0" />
                      {item}
                    </li>
                  ))}
                </ul>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Simple Award Giving */}
      <section className="py-20 bg-gradient-to-br from-blue-50 to-purple-50">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-7xl">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
            >
              <div className="inline-flex items-center gap-2 bg-blue-100 text-blue-700 px-4 py-2 rounded-full text-sm font-medium mb-4">
                <Gift className="h-4 w-4" />
                Simple Process
              </div>
              <h2 className="text-3xl lg:text-4xl font-bold text-gray-900 mb-6 leading-tight">
                Streamlined award giving process
              </h2>
              <p className="text-lg text-gray-600 mb-8 leading-relaxed">
                Make recognition easy with our intuitive award giving interface. From selection to approval, every step is designed for simplicity and impact.
              </p>
              <div className="space-y-4">
                <div className="flex items-start gap-3">
                  <div className="w-8 h-8 bg-blue-600 rounded-full flex items-center justify-center text-white text-sm font-bold flex-shrink-0">
                    1
                  </div>
                  <div>
                    <h3 className="text-sm font-semibold text-gray-900 mb-1">Select Award</h3>
                    <p className="text-gray-600">Choose from available awards</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <div className="w-8 h-8 bg-blue-600 rounded-full flex items-center justify-center text-white text-sm font-bold flex-shrink-0">
                    2
                  </div>
                  <div>
                    <h3 className="text-sm font-semibold text-gray-900 mb-1">Choose Recipients</h3>
                    <p className="text-gray-600">Search and select team members</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <div className="w-8 h-8 bg-blue-600 rounded-full flex items-center justify-center text-white text-sm font-bold flex-shrink-0">
                    3
                  </div>
                  <div>
                    <h3 className="text-sm font-semibold text-gray-900 mb-1">Add Details</h3>
                    <p className="text-gray-600">Set point values and personal messages</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <div className="w-8 h-8 bg-blue-600 rounded-full flex items-center justify-center text-white text-sm font-bold flex-shrink-0">
                    4
                  </div>
                  <div>
                    <h3 className="text-sm font-semibold text-gray-900 mb-1">Submit & Celebrate</h3>
                    <p className="text-gray-600">Send for approval or auto-publish</p>
                  </div>
                </div>
              </div>
            </motion.div>
            
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
              className="relative"
            >
              <img 
                src="https://downloads.intercomcdn.com/i/o/vnd9nsk2/1344508168/d11b8646064e2af518a04314deca/Screenshot+2025-01-21+at+11_53_00.png?expires=1752982200&signature=6c6edf9647a21c9e62cf0c1ac7b7dfbac405cb7d1c892fe4a908db0c87a1851f&req=dSMjEsx%2BlYBZUfMW1HO4zTbk%2FNUyQA2qfsG8Es8YZBc9hkMAz%2FMyJhihKPVl%0AScGETgMQwoNWqc0fek8%3D%0A" 
                alt="Awards giving interface with selection and configuration options" 
                className="w-full rounded-3xl shadow-2xl" 
              />
            </motion.div>
          </div>
        </div>
      </section>

      {/* Flexible Approvals */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-7xl">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
              className="order-2 lg:order-1"
            >
              <img 
                src="https://downloads.intercomcdn.com/i/o/vnd9nsk2/1344618675/36f03e3dedb4dfdb43c3810a8ff6/Screenshot+2025-01-21+at+13_20_15.png?expires=1751758200&signature=b8770f3c7aa2b48976d6944204525b377266a4df3f809cd45b104d628d3d262b&req=dSMjEs9%2FlYdYXPMW1HO4zVTCWkZtTU9xgJDhmlqcB5O%2B%2FYD8LJAJUabkRgwY%0AZ4DGnuyg%2BKGCTsLQjek%3D%0A" 
                alt="Awards configuration dashboard with approval workflow settings" 
                className="w-full rounded-3xl shadow-2xl" 
              />
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
              className="order-1 lg:order-2"
            >
              <div className="inline-flex items-center gap-2 bg-purple-100 text-purple-700 px-4 py-2 rounded-full text-sm font-medium mb-4">
                <Shield className="h-4 w-4" />
                Flexible Control
              </div>
              <h2 className="text-3xl lg:text-4xl font-bold text-gray-900 mb-6 leading-tight">
                Sophisticated approval workflows
              </h2>
              <p className="text-lg text-gray-600 mb-8 leading-relaxed">
                Configure approval processes with up to 4 levels. Set manager approvals, custom approver groups, or auto-approve for streamlined recognition.
              </p>
              <div className="space-y-4">
                <div className="flex items-center gap-3">
                  <div className="w-2 h-2 bg-blue-500 rounded-full"></div>
                  <span className="text-gray-700">Up to 4 approval levels</span>
                </div>
                <div className="flex items-center gap-3">
                  <div className="w-2 h-2 bg-blue-500 rounded-full"></div>
                  <span className="text-gray-700">Manager cascading approval</span>
                </div>
                <div className="flex items-center gap-3">
                  <div className="w-2 h-2 bg-blue-500 rounded-full"></div>
                  <span className="text-gray-700">Custom approver groups</span>
                </div>
                <div className="flex items-center gap-3">
                  <div className="w-2 h-2 bg-blue-500 rounded-full"></div>
                  <span className="text-gray-700">Auto-approve option for instant publishing</span>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="py-20 bg-gradient-to-br from-blue-600 to-purple-700 text-white">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-7xl">
          <div className="text-center mb-16">
            <h2 className="text-3xl lg:text-4xl font-bold mb-6">
              The Impact of Structured Awards Programs
            </h2>
            <p className="text-xl text-blue-100 max-w-3xl mx-auto leading-relaxed">
              Organizations using Assembly's awards platform see measurable improvements in recognition and engagement
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {benefits.map((benefit, index) => (
              <motion.div
                key={benefit.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                className="bg-white/10 backdrop-blur-sm rounded-2xl p-8 text-center hover:bg-white/20 transition-all duration-300"
              >
                <div className="w-12 h-12 mx-auto bg-white/20 rounded-full flex items-center justify-center mb-4">
                  {benefit.icon}
                </div>
                <h3 className="text-xl font-bold mb-3">{benefit.title}</h3>
                <p className="text-blue-100 leading-relaxed">{benefit.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-4xl">
          <div className="text-center mb-12">
            <h2 className="text-3xl lg:text-4xl font-bold text-gray-900 mb-4">
              Frequently Asked Questions
            </h2>
            <p className="text-lg text-gray-600">
              Everything you need to know about Employee Awards
            </p>
          </div>

          <Accordion type="single" collapsible className="space-y-4">
            <AccordionItem value="item-1" className="border rounded-lg px-6">
              <AccordionTrigger className="text-left hover:no-underline">
                <span className="text-lg font-semibold">What types of awards can I create?</span>
              </AccordionTrigger>
              <AccordionContent className="text-gray-600 leading-relaxed">
                You can create any type of award that fits your culture — Employee of the Month, Innovation Awards, Leadership Recognition, Values Champions, Safety Awards, and more. Each award is fully customizable with its own image, description, budget, and approval workflow.
              </AccordionContent>
            </AccordionItem>

            <AccordionItem value="item-2" className="border rounded-lg px-6">
              <AccordionTrigger className="text-left hover:no-underline">
                <span className="text-lg font-semibold">How do approval workflows work?</span>
              </AccordionTrigger>
              <AccordionContent className="text-gray-600 leading-relaxed">
                You can configure up to 4 levels of approval for each award. Options include manager of recipient approval, custom approver groups, department-based approvers, or auto-approve for instant publishing. This ensures the right level of oversight for each type of recognition.
              </AccordionContent>
            </AccordionItem>

            <AccordionItem value="item-3" className="border rounded-lg px-6">
              <AccordionTrigger className="text-left hover:no-underline">
                <span className="text-lg font-semibold">Can I control budgets and point allocation?</span>
              </AccordionTrigger>
              <AccordionContent className="text-gray-600 leading-relaxed">
                Yes! You can set total budgets for each award, configure refresh cycles (monthly, quarterly, annually), set per-award point limits, and choose whether to include swag points. This gives you complete control over recognition spending.
              </AccordionContent>
            </AccordionItem>

            <AccordionItem value="item-4" className="border rounded-lg px-6">
              <AccordionTrigger className="text-left hover:no-underline">
                <span className="text-lg font-semibold">Who can give awards in my organization?</span>
              </AccordionTrigger>
              <AccordionContent className="text-gray-600 leading-relaxed">
                You have complete control over giver permissions. You can allow everyone to give awards, restrict to admins or managers only, or create custom groups based on departments, locations, or roles. This flexibility ensures the right people can recognize outstanding work.
              </AccordionContent>
            </AccordionItem>

            <AccordionItem value="item-5" className="border rounded-lg px-6">
              <AccordionTrigger className="text-left hover:no-underline">
                <span className="text-lg font-semibold">How are awards different from regular recognition?</span>
              </AccordionTrigger>
              <AccordionContent className="text-gray-600 leading-relaxed">
                Awards are prestigious, structured recognition programs with dedicated budgets and approval workflows. They automatically create special recognition posts when given, making them highly visible celebrations. They're perfect for milestone achievements, monthly/quarterly recognition, and values-based honors.
              </AccordionContent>
            </AccordionItem>

            <AccordionItem value="item-6" className="border rounded-lg px-6">
              <AccordionTrigger className="text-left hover:no-underline">
                <span className="text-lg font-semibold">Can I track award impact and usage?</span>
              </AccordionTrigger>
              <AccordionContent className="text-gray-600 leading-relaxed">
                Absolutely! You can see award usage metrics, track budget utilization, view who's giving and receiving awards, and understand engagement trends over time. This data helps you optimize your awards program and demonstrate ROI.
              </AccordionContent>
            </AccordionItem>
          </Accordion>
        </div>
      </section>

      {/* Create a hidden section for the demo to scroll to */}
      <div id="demo" className="scroll-mt-24"></div>
      
      <DemoBookingModal 
        open={isDemoModalOpen} 
        onOpenChange={setIsDemoModalOpen} 
      />
    </ProductPageLayout>
  );
};

export default Awards;
